<template>
  <v-card>
    <v-card-title class="title">
      Security Verification
    </v-card-title>
    <v-divider />
    <v-card-text class="pt-4 pb-2 px-4">
      <!-- Locked state -->
      <div v-if="isLocked" class="text-xs-center">
        <p class="error--text font-weight-bold">
          Access Locked
        </p>
        <p class="mt-3">
          You've been locked out due to too many failed verification attempts.
          The administrator has been notified and will contact you if needed.
        </p>
        <p class="mt-3 grey--text">
          Please contact your tax preparer for assistance.
        </p>
      </div>

      <!-- Initial state - no code requested -->
      <div v-else-if="!codeRequested" class="text-xs-center">
        <v-alert
          v-if="expiredCodeMessage"
          type="warning"
          class="mb-3 text-xs-left"
        >
          {{ expiredCodeMessage }}
        </v-alert>
        <p class="body-1 mb-4">
          To keep your information secure, we'll send a one-time verification
          code to your email to make sure it's really you. Enter the code on the
          next screen to continue.
        </p>
      </div>

      <!-- Code requested - show input -->
      <div v-else>
        <p>
          We've sent a one-time code to your email. Enter it below to safely
          access your K-1 package.
        </p>

        <v-text-field
          ref="codeInput"
          v-model="verificationCode"
          label="Email Verification Code"
          placeholder="Enter 6-character code"
          type="text"
          :disabled="isVerifying"
          :error-messages="errorMessage"
          @input="formatCode"
          @keyup.enter="verifyCode"
          maxlength="6"
          class="mt-4"
          solo
          autocomplete="off"
        />

        <div
          v-if="attemptsRemaining !== null && attemptsRemaining < 5"
          class="mt-2 grey--text text-xs-center"
        >
          {{ attemptsRemaining }} attempt{{
            attemptsRemaining !== 1 ? 's' : ''
          }}
          remaining
        </div>

        <div
          v-if="expiresInMinutes"
          class="mt-2 grey--text text--darken-1 text-xs-center"
        >
          Code expires in {{ expiresInMinutes }} minutes
        </div>
      </div>
    </v-card-text>
    <v-card-actions class="pb-3 px-4">
      <!-- Locked state - no actions -->
      <template v-if="isLocked">
        <!-- No buttons when locked -->
      </template>

      <!-- Initial state - request code button -->
      <v-btn
        v-else-if="!codeRequested"
        color="primary"
        class="text-none mx-auto"
        large
        block
        style="min-height: 44px;"
        @click="requestCode"
        :loading="isRequestingCode"
        :disabled="isRequestingCode"
      >
        Send Verification Code
      </v-btn>

      <!-- Code requested - verify and resend buttons -->
      <v-layout v-else column>
        <v-btn
          color="primary"
          class="text-none mt-3 mb-3"
          large
          block
          style="min-height: 44px;"
          @click="verifyCode"
          :disabled="!isValidCode || isVerifying"
          :loading="isVerifying"
        >
          Verify Code
        </v-btn>
        <v-btn
          flat
          class="text-none"
          large
          block
          style="min-height: 44px; margin-left: 0;"
          @click="requestCode"
          :disabled="isOnCooldown || isRequestingCode"
          :loading="isRequestingCode"
        >
          Send New Code{{ cooldownText }}
        </v-btn>
      </v-layout>
    </v-card-actions>
  </v-card>
</template>

<script>
import API from '@/api';

export default {
  props: {
    token: {
      type: String,
      required: true,
    },
    taxReturnId: {
      type: Number,
      required: true,
    },
    partnerEmail: {
      type: String,
      required: true,
    },
    initialLocked: {
      type: Boolean,
      default: false,
    },
    initialCodeRequested: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      verificationCode: '',
      codeRequested: this.initialCodeRequested,
      isRequestingCode: false,
      isVerifying: false,
      errorMessage: '',
      expiredCodeMessage: '',
      attemptsRemaining: null,
      cooldownSeconds: 0,
      isOnCooldown: false,
      expiresInMinutes: null,
      cooldownInterval: null,
      expirationInterval: null,
      isLocked: this.initialLocked,
    };
  },

  computed: {
    isValidCode() {
      return /^[A-Za-z0-9]{6}$/.test(this.verificationCode);
    },
    cooldownText() {
      return this.cooldownSeconds > 0 ? ` (${this.cooldownSeconds}s)` : '';
    },
  },

  methods: {
    formatCode() {
      // Allow only alphanumeric characters (A-Z, a-z, 0-9) and convert to uppercase
      this.verificationCode = this.verificationCode
        .replace(/[^A-Za-z0-9]/g, '')
        .toUpperCase();
      // Clear error when user types
      if (this.errorMessage) {
        this.errorMessage = '';
      }
    },

    refocusInput() {
      // Refocus the input after clearing on error
      this.$nextTick(() => {
        if (this.$refs.codeInput) {
          this.$refs.codeInput.focus();
        }
      });
    },

    async requestCode() {
      this.isRequestingCode = true;
      this.errorMessage = '';
      this.expiredCodeMessage = '';

      try {
        const response = await API.taxReturn.requestK1VerificationCode({
          taxReturnId: this.taxReturnId,
          token: this.token,
        });

        // Success - code sent
        this.codeRequested = true;
        this.verificationCode = '';
        this.attemptsRemaining = null;

        // Start expiration timer
        this.startExpirationTimer(response.data.expires_in_seconds);

        // Start 2-minute cooldown
        this.startCooldown(120);

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Verification code sent! Please check your email.',
        });
      } catch (error) {
        console.error('Error requesting verification code:', error);

        if (error.response) {
          const data = error.response.data;

          if (
            error.response.status === 403 &&
            data.error === 'verification_locked'
          ) {
            // User is locked out
            this.isLocked = true;

            // Clean up timers when locked
            if (this.cooldownInterval) {
              clearInterval(this.cooldownInterval);
              this.cooldownInterval = null;
            }
            if (this.expirationInterval) {
              clearInterval(this.expirationInterval);
              this.expirationInterval = null;
            }

            this.$store.dispatch('application/setErrorNotification', {
              message:
                data.message ||
                'This K-1 package has been locked. Please contact the sender.',
            });
          } else if (error.response.status === 429) {
            // Rate limited
            this.startCooldown(data.retry_after_seconds || 120);
            this.errorMessage =
              data.message || 'Please wait before requesting another code';
            this.$store.dispatch('application/setErrorNotification', {
              message:
                data.message || 'Please wait before requesting another code',
            });
          } else {
            this.errorMessage =
              data.message || 'Failed to send verification code';
            this.$store.dispatch('application/setErrorNotification', {
              message: this.errorMessage,
            });
          }
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message:
              'Network error. Please check your connection and try again.',
          });
        }
      } finally {
        this.isRequestingCode = false;
      }
    },

    async verifyCode() {
      if (!this.isValidCode) return;

      this.isVerifying = true;
      this.errorMessage = '';

      try {
        await API.taxReturn.verifyK1Code({
          taxReturnId: this.taxReturnId,
          token: this.token,
          code: this.verificationCode,
          partnerEmail: this.partnerEmail, // Pass partnerEmail for token storage
        });

        // Success! Token is automatically stored by verifyK1Code API method
        this.$emit('verified');
      } catch (error) {
        if (error.response) {
          const data = error.response.data;

          if (error.response.status === 401) {
            // Invalid code or expired
            if (data.error === 'code_expired') {
              this.expiredCodeMessage =
                data.message ||
                'Your verification code has expired. Please request a new one.';
              this.verificationCode = '';
              this.attemptsRemaining = null;
              this.expiresInMinutes = null;
              if (this.expirationInterval) {
                clearInterval(this.expirationInterval);
                this.expirationInterval = null;
              }
              this.codeRequested = false; // Allow requesting new code
            } else {
              this.errorMessage = data.message || 'Invalid verification code';
              this.attemptsRemaining = data.attempts_remaining;
              this.verificationCode = ''; // Clear for retry
              this.refocusInput();
            }
          } else if (error.response.status === 403) {
            // Too many attempts - now locked
            if (data.error === 'too_many_attempts') {
              this.isLocked = true;

              // Clean up timers when locked
              if (this.cooldownInterval) {
                clearInterval(this.cooldownInterval);
                this.cooldownInterval = null;
              }
              if (this.expirationInterval) {
                clearInterval(this.expirationInterval);
                this.expirationInterval = null;
              }

              this.$store.dispatch('application/setErrorNotification', {
                message:
                  data.message ||
                  'Too many failed attempts. This K-1 package has been locked. Please contact the sender.',
              });
            } else {
              this.errorMessage = data.message || 'Access forbidden';
            }
            this.verificationCode = '';
            this.refocusInput();
          } else if (error.response.status === 422) {
            // Validation error or no code generated
            this.errorMessage = data.message || data.error || 'Invalid code';
            if (data.error === 'no_code_generated') {
              this.codeRequested = false;
            } else {
              this.verificationCode = '';
              this.refocusInput();
            }
          } else {
            this.errorMessage =
              data.message || 'Verification failed. Please try again.';
            this.verificationCode = '';
            this.refocusInput();
          }
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message:
              'Network error. Please check your connection and try again.',
          });
          this.refocusInput();
        }
      } finally {
        this.isVerifying = false;
      }
    },

    startCooldown(seconds) {
      this.cooldownSeconds = seconds;
      this.isOnCooldown = true;

      // Clear any existing interval
      if (this.cooldownInterval) {
        clearInterval(this.cooldownInterval);
      }

      this.cooldownInterval = setInterval(() => {
        this.cooldownSeconds--;
        if (this.cooldownSeconds <= 0) {
          clearInterval(this.cooldownInterval);
          this.cooldownInterval = null;
          this.isOnCooldown = false;
        }
      }, 1000);
    },

    startExpirationTimer(seconds) {
      this.expiresInMinutes = Math.floor(seconds / 60);

      // Clear any existing interval
      if (this.expirationInterval) {
        clearInterval(this.expirationInterval);
      }

      let remainingSeconds = seconds;
      this.expirationInterval = setInterval(() => {
        remainingSeconds -= 60;
        if (remainingSeconds <= 0) {
          clearInterval(this.expirationInterval);
          this.expirationInterval = null;
          this.expiresInMinutes = null;
          this.expiredCodeMessage =
            'Your verification code has expired. Please request a new one.';
          this.codeRequested = false; // Reset to allow new request
        } else {
          this.expiresInMinutes = Math.floor(remainingSeconds / 60);
        }
      }, 60000);
    },
  },

  watch: {
    initialLocked(newVal) {
      this.isLocked = newVal;
    },
  },

  beforeDestroy() {
    // Clean up intervals
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
    if (this.expirationInterval) {
      clearInterval(this.expirationInterval);
    }
  },
};
</script>
